import { extractSubdomain } from '@/helpers/extractSubdomain';
// import { getUrlParams } from '@/helpers/getUrlParams';
import { normalizeSearchParams } from '@/helpers/normalizeSearchParams';
import { windowIsReady } from '@/helpers/windowIsReady';
import { ENV } from '@/constants';

function getWebAppBaseUrl(): string {
	if (typeof window === 'undefined') {
		return ENV.LIFT_WEB_APP_URL;
	}

	const raw = window.location.href;
	const subdomain = extractSubdomain(raw);

	if (!subdomain) {
		return ENV.LIFT_WEB_APP_URL;
	}

	return `https://${subdomain}.liftapp.ai/editor`;
}

const webAppBase = getWebAppBaseUrl();

function getCurrentSearchParams(): URLSearchParams {
	return new URLSearchParams(windowIsReady ? window.location.search : '');
}

function buildFunnelUrl(segment: string): string {
	return `${webAppBase}/funnel-auth/${segment}?${getCurrentSearchParams()}`;
}

export const GenerateLandingLinkService = {
	// Info: keep it just in case of necessary
	// appStore(params?: Record<string, unknown>) {
	// 	const {
	// 		utm_campaign,
	// 		af_ios_url,
	// 		campaign_id,
	// 		utm_term,
	// 	}: Record<string, unknown> = getUrlParams();
	//
	// 	const queryParams = {
	// 		af_force_deeplink: true,
	// 		af_dp: 'lift://',
	// 		c: utm_campaign || 'webapp_landing',
	// 		pid: 'Install_from_wp',
	// 		af_xp: 'custom',
	// 		af_c_id: campaign_id || 'webapp_landing',
	// 		af_keyword: utm_term,
	// 		af_ios_url,
	// 		...params,
	// 	};
	//
	// 	const searchParams = normalizeSearchParams<string>(queryParams, 'string');
	// 	return `https://lift.onelink.me/KndT?${searchParams}`;
	// },

	/** Standalone checkout in the web app (/editor/payment) */
	webAppPayment(params?: Record<string, unknown>) {
		const queryParams = {
			...Object.fromEntries(getCurrentSearchParams()),
			...params,
		};
		const searchParams = normalizeSearchParams<string>(queryParams, 'string');
		return `${webAppBase}/payment${searchParams ? `?${searchParams}` : ''}`;
	},

	webApp(params?: Record<string, unknown>) {
		const queryParams = {
			...Object.fromEntries(getCurrentSearchParams()),
			...params,
			auto_login: true,
		};
		const searchParams = normalizeSearchParams<string>(queryParams, 'string');
		return `${webAppBase}/sign-in?${searchParams}`;
	},

	// Info: keep it just in case of necessary
	// androidStore(params?: Record<string, unknown>) {
	// 	const { utm_campaign, campaign_id, utm_term }: Record<string, unknown> =
	// 		getUrlParams();
	//
	// 	const playStore =
	// 		'https://play.google.com/store/apps/details?id=com.lift.android.app&hl=en&gl=US';
	// 	const queryParams = {
	// 		af_dp: 'lift://',
	// 		c: utm_campaign || 'webapp_landing',
	// 		pid: 'Install_from_wp',
	// 		af_xp: 'custom',
	// 		af_c_id: campaign_id || 'webapp_landing',
	// 		af_keyword: utm_term,
	// 		af_android_url: playStore,
	// 		af_ios_url: playStore,
	// 		af_web_dp: playStore,
	// 		...params,
	// 	};
	//
	// 	const searchParams = normalizeSearchParams<string>(queryParams, 'string');
	// 	return `https://lift.onelink.me/KndT?${searchParams}`;
	// },

	funnelRemoveBGV2() {
		return buildFunnelUrl('remove-bg-v2');
	},

	funnelRemoveBGV3() {
		return buildFunnelUrl('remove-bg-v3');
	},

	funnelRemoveBGV4() {
		return buildFunnelUrl('remove-bg-v4');
	},

	funnelImageGenerator() {
		return buildFunnelUrl('image-generator');
	},

	funnelImageGeneratorV2() {
		return buildFunnelUrl('image-generator-v2');
	},

	funnelMagicEraser() {
		return buildFunnelUrl('magic-eraser-v3');
	},

	funnelObjectRemover() {
		return buildFunnelUrl('object-remover');
	},

	funnelObjectRemoverV2() {
		return buildFunnelUrl('object-remover-v2');
	},

	funnelImageEnhancement() {
		return buildFunnelUrl('image-enhancement');
	},

	funnelImageEnhancementV2() {
		return buildFunnelUrl('image-enhancement-v2');
	},

	funnelImageEnhancementV3() {
		return buildFunnelUrl('image-enhancement-v3');
	},

	funnelImageUpscale() {
		return buildFunnelUrl('image-upscale');
	},
	funnelImageUpscaleV2() {
		let params = new URLSearchParams(
			windowIsReady ? window.location.search : '',
		);
		return `${ENV.LIFT_WEB_APP_URL}/funnel-auth/image-upscale-v2?${params}`;
	},
	funnelImageUpscaleV2_2() {
		return buildFunnelUrl('image-upscale-v2');
	},

	funnelImageUpscaleV3() {
		return buildFunnelUrl('image-upscale-v3');
	},

	funnelImageUpscaleV4() {
		return buildFunnelUrl('image-upscale-v4');
	},

	funnelImageAiFilterLego() {
		return buildFunnelUrl('ai-filters-lego');
	},

	funnelImageAiFilterGhibli() {
		return buildFunnelUrl('ai-filters-ghibli');
	},

	funnelImageAiFilterStarterPack() {
		return buildFunnelUrl('ai-filters-starter-pack');
	},

	funnelHeadshotGenerator() {
		return buildFunnelUrl('ai-headshot');
	},

	funnelHeadshotGeneratorV2() {
		return buildFunnelUrl('ai-headshot-v2'); // non trial
	},

	funnelHeadshotGeneratorV3() {
		return buildFunnelUrl('ai-headshot-v3'); // non tier1 geo
	},

	funnelWatermarkRemover() {
		return buildFunnelUrl('watermark-remover');
	},
	funnelWatermarkRemoverV2() {
		return buildFunnelUrl('watermark-remover-v2');
	},

	funnelTextToVideo() {
		return buildFunnelUrl('text-to-video');
	},
	funnelTextToVideoV2() {
		return buildFunnelUrl('text-to-video-v2');
	},
	// not used anymore
	funnelImageToVideo() {
		return buildFunnelUrl('image-to-video');
	},

	funnelAiImageCombiner() {
		return buildFunnelUrl('image-combiner');
	},

	funnelAiImageCombinerV2() {
		return buildFunnelUrl('image-combiner-v2');
	},

	funnelAnimeGenerator() {
		return buildFunnelUrl('anime-generator');
	},

	funnelCartoonGenerator() {
		return buildFunnelUrl('cartoon-generator');
	},

	funnelImageExtender() {
		return buildFunnelUrl('image-extender');
	},

	funnelImageExtenderV2() {
		return buildFunnelUrl('image-extender-v2');
	},

	funnelImageToText() {
		return buildFunnelUrl('image-to-text');
	},

	funnelHairStyle() {
		return buildFunnelUrl('hairstyle-changer');
	},

	funnelHairStyleV2() {
		return buildFunnelUrl('hairstyle-changer-v2');
	},

	funnelHairStyleV3() {
		return buildFunnelUrl('hairstyle-changer-v3');
	},

	funnelClothesChanger() {
		return buildFunnelUrl('clothes-changer');
	},

	funnelPhotoColorizer() {
		return buildFunnelUrl('photo-colorizer');
	},
	funnelPhotoColorizerV2() {
		return buildFunnelUrl('photo-colorizer-v2');
	},
	funnelRecolor() {
		return buildFunnelUrl('ai-recolor');
	},
	funnelAiEditor() {
		return buildFunnelUrl('image-editor');
	},
	funnelGenerativeFill() {
		return buildFunnelUrl('generative-fill');
	},
	funnelAiFaceSwap() {
		return buildFunnelUrl('face-swap');
	},
	funnelAIBackground() {
		return buildFunnelUrl('background-changer');
	},
	funnelPassportPhotoMaker() {
		return buildFunnelUrl('passport-photo-maker');
	},
	funnelPassportPhotoMakerV2() {
		return buildFunnelUrl('passport-photo-maker-v2');
	},
	funnelTattooGenerator() {
		return buildFunnelUrl('tattoo-generator');
	},
	funnelFontGenerator() {
		return buildFunnelUrl('font-generator');
	},
	funnelAiLogoGenerator() {
		return buildFunnelUrl('logo-generator');
	},
	funnelImageConverter() {
		return buildFunnelUrl('image-converter');
	},
	funnelImageConverterV2() {
		return buildFunnelUrl('image-converter-v2');
	},
	funnelVideoConverter() {
		return buildFunnelUrl('video-converter');
	},
	funnelVideoCompressor() {
		return buildFunnelUrl('video-compressor');
	},
	funnelVideoBackgroundRemover() {
		return buildFunnelUrl('video-bg-remover');
	},
	funnelImageToHeic() {
		return buildFunnelUrl('image-converter');
	},
	funnelWebpConverter() {
		return buildFunnelUrl('image-converter');
	},
	funnelTiffToPng() {
		return buildFunnelUrl('image-converter');
	},
	funnelImageCompressor() {
		return buildFunnelUrl('image-compressor');
	},
	funnelImageCompressorV2() {
		return buildFunnelUrl('image-compressor-v2');
	},
	funnelImageResizer() {
		return buildFunnelUrl('image-resizer');
	},
	funnelImageResizerV2() {
		return buildFunnelUrl('image-resizer-v2');
	},
	funnelCropImage() {
		return buildFunnelUrl('image-crop');
	},
	funnelFlipRotateImage() {
		return buildFunnelUrl('flip-rotate');
	},
	funnelAiImageDetector() {
		return buildFunnelUrl('image-detector');
	},
	funnelAiImageTranslator() {
		return buildFunnelUrl('image-translator');
	},
	funnelAiBabyGenerator() {
		return buildFunnelUrl('see-my-future-baby');
	},
	funnelBlur() {
		return buildFunnelUrl('image-blur');
	},
	funnelAgeProgression() {
		return buildFunnelUrl('age-progression');
	},
	funnelAgeProgressionV2() {
		return buildFunnelUrl('age-progression-v2');
	},
	funnelImageSplitter() {
		return buildFunnelUrl('image-splitter');
	},
	funnelImageSplitterV2() {
		return buildFunnelUrl('image-splitter-v2');
	},
	funnelMetaFashionAiHeadshot() {
		return buildFunnelUrl('meta-fashion-ai-headshot');
	},
	funnelMetaSpicyAiHeadshot() {
		return buildFunnelUrl('meta-spicy-ai-headshot');
	},
	funnelMetaPregnancyAiHeadshot() {
		return buildFunnelUrl('meta-pregnancy-ai-headshot');
	},
	funnelFaceShapeDetector() {
		return buildFunnelUrl('face-shape-detector');
	},
	funnelColorPicker() {
		return buildFunnelUrl('color-picker');
	},
	funnelAiRelight() {
		return buildFunnelUrl('ai-relight');
	},
	funnelAiRelightV1() {
		return buildFunnelUrl('ai-relight-v1');
	},
	funnelAiRetake() {
		return buildFunnelUrl('ai-retake');
	},
	funnelQrCodeGenerator() {
		return buildFunnelUrl('qr-code-generator');
	},
	funnelBarcodeGenerator() {
		return buildFunnelUrl('barcode-generator');
	},
	funnelVideoEnhancer() {
		return buildFunnelUrl('video-enhancer');
	},
	funnelVoiceGenerator() {
		return buildFunnelUrl('voice-generator');
	},
	funnelMusicGenerator() {
		return buildFunnelUrl('music-generator');
	},
	funnelSpeechToText() {
		return buildFunnelUrl('speech-to-text');
	},
};
